if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        // Add loading class while function is runing
          document.querySelector(".buy-buttons-wrapper").classList.add('product__info-loading');
        return (html) => {
          
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          const isSoldOut =
          html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;

          if (isSoldOut) {
            document.querySelector('.product-form__buttons')?.classList.add('current_variant_unavailable');
            document.querySelector('#BIS_trigger')?.classList.remove('hidden');
            document.querySelector('#tote-button')?.classList.add('hidden');
            document.querySelector('quantity-input')?.classList.add('disabled');
          } else {
            document.querySelector('.product-form__buttons')?.classList.remove('current_variant_unavailable');
            document.querySelector('#BIS_trigger')?.classList.add('hidden');
            document.querySelector('#tote-button')?.classList.remove('hidden');
            document.querySelector('quantity-input')?.classList.remove('disabled');
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
           document.querySelector(".buy-buttons-wrapper").classList.remove('product__info-loading');
        };
      }

      updateVariantInputs(variantId) {
        
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
class VariantDropdown extends HTMLElement {
  constructor() {
    super();
    this.toggleButton = this.querySelector('.variant_select_button');
    this.fieldset = this.querySelector('fieldset');
    this.closeButton = this.querySelector('.size_close_button');
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
  }

  connectedCallback() {
    if (!this.toggleButton || !this.fieldset) return;

    // Toggle on click
    this.toggleButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleDropdown();
    });

    // Toggle on Enter or Space
    this.toggleButton.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleDropdown();
      }
    });

    // Close when selecting a radio (your existing logic)
    const radios = this.fieldset.querySelectorAll('input[type="radio"]');
    radios.forEach((radio) => {
      radio.addEventListener('change', () => this.closeDropdown());
    });

    // Arrow navigation + Enter/Space handling
    this.fieldset.addEventListener('keydown', this.handleKeyDown);

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeDropdown();
      });
    }
  }

  toggleDropdown() {
    const isOpen = this.fieldset.classList.toggle('is-open');
    document.body.classList.toggle('variants_overlay', isOpen);
    document.body.classList.toggle('overflow-hidden', isOpen);

    if (isOpen) {
      // Wait until visible before focusing
      requestAnimationFrame(() => {
        const checkedRadio = this.fieldset.querySelector('input[type="radio"]:checked');
        const labelToFocus = checkedRadio
          ? this.fieldset.querySelector(`label[for="${checkedRadio.id}"]`)
          : this.fieldset.querySelector('label');

        if (labelToFocus) {
          labelToFocus.setAttribute('tabindex', '0'); // Make it focusable
          labelToFocus.focus();
        }
      });

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleEscapeKey);
    } else {
      this.cleanupListeners();
      this.toggleButton.focus();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.closeDropdown();
      this.toggleButton.focus();
    }
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  handleKeyDown(event) {
    // Only consider non-disabled items (skips those with class "disabled")
    const radios = Array.from(this.fieldset.querySelectorAll('input[type="radio"]:not(.disabled)'));

    // Normalize current radio even if a LABEL is focused
    const active = document.activeElement;
    let currentRadio = null;
    if (active && active.tagName === 'LABEL') {
      const forId = active.getAttribute('for');
      if (forId) currentRadio = document.getElementById(forId);
    } else if (active && active.tagName === 'INPUT' && active.type === 'radio') {
      currentRadio = active;
    }

    const currentIndex = currentRadio ? radios.indexOf(currentRadio) : -1;

    // Arrow navigation
    if ((event.key === 'ArrowDown' || event.key === 'ArrowRight') && currentIndex !== -1) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % radios.length;
      const nextLabel = this.fieldset.querySelector(`label[for="${radios[nextIndex].id}"]`);
      if (nextLabel) {
        nextLabel.setAttribute('tabindex', '0');
        nextLabel.focus();
      }
      return;
    }

    if ((event.key === 'ArrowUp' || event.key === 'ArrowLeft') && currentIndex !== -1) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + radios.length) % radios.length;
      const prevLabel = this.fieldset.querySelector(`label[for="${radios[prevIndex].id}"]`);
      if (prevLabel) {
        prevLabel.setAttribute('tabindex', '0');
        prevLabel.focus();
      }
      return;
    }

    // Enter / Space should select the radio when focused on LABEL or INPUT
    if (event.key === 'Enter' || event.key === ' ') {
      const isSpace = event.key === ' ';
      // Prevent page scroll for Space
      event.preventDefault();

      if (active && active.tagName === 'LABEL') {
        const forId = active.getAttribute('for');
        if (!forId) return;
        const input = document.getElementById(forId);
        if (input && !input.classList.contains('disabled')) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (active && active.tagName === 'INPUT' && active.type === 'radio') {
        if (!active.classList.contains('disabled')) {
          active.checked = true;
          active.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  closeDropdown() {
    this.fieldset.classList.remove('is-open');
    document.body.classList.remove('variants_overlay');
    document.body.classList.remove('overflow-hidden');
    this.cleanupListeners();
    this.toggleButton.focus();
  }

  cleanupListeners() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleEscapeKey);

    // Remove temporary tabindex from labels
    this.fieldset.querySelectorAll('label[tabindex="0"]').forEach(label => {
      label.removeAttribute('tabindex');
    });
  }
}

customElements.define('variant-dropdown', VariantDropdown);


class PdpCollapsible extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.pdp-collapsible__button');
    this.panel = this.querySelector('.pdp-collapsible__panel');
    this.plusIcon = this.querySelector('.plus_icon');
    this.minusIcon = this.querySelector('.minus_icon');
  }

  connectedCallback() {
    if (!this.button || !this.panel) return;

    this.button.addEventListener('click', () => {
      const isOpen = this.panel.classList.contains('pdp-collapsible--active');

      // Close all other panels in the document
      document.querySelectorAll('pdp-collapsible').forEach((el) => {
        if (el !== this) {
          el.querySelector('.pdp-collapsible__button')?.classList.remove('pdp-collapsible--active');
          const otherPanel = el.querySelector('.pdp-collapsible__panel');
          otherPanel?.classList.remove('pdp-collapsible--active');
          if (otherPanel) otherPanel.style.maxHeight = null;

          const plus = el.querySelector('.plus_icon');
          const minus = el.querySelector('.minus_icon');
          if (plus && minus) {
            plus.style.display = 'inline-block';
            minus.style.display = 'none';
          }
        }
      });

      // Toggle current panel
      this.button.classList.toggle('pdp-collapsible--active', !isOpen);
      this.panel.classList.toggle('pdp-collapsible--active', !isOpen);
      this.panel.style.maxHeight = isOpen ? null : this.panel.scrollHeight + 'px';

      // Toggle icons for current
      if (this.plusIcon && this.minusIcon) {
        this.plusIcon.style.display = isOpen ? 'inline-block' : 'none';
        this.minusIcon.style.display = isOpen ? 'none' : 'inline-block';
      }
    });

    // Set initial icon visibility
    if (this.plusIcon && this.minusIcon) {
      this.plusIcon.style.display = 'inline-block';
      this.minusIcon.style.display = 'none';
    }
  }
}

customElements.define('pdp-collapsible', PdpCollapsible);
class PdpSizingToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.pdp_sizing_btn');
    this.panel = this.querySelector('.pdp_sizing');
    this.closeButton = this.querySelector('.pdp_sizing_close');

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
  }

  connectedCallback() {
    if (!this.button || !this.panel) return;

    // Make panel focusable if it isn't
    if (!this.panel.hasAttribute('tabindex')) {
      this.panel.setAttribute('tabindex', '-1');
    }

    // Toggle handler
    this.button.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.panel.classList.contains('is-open')) {
        this.closePanel();
      } else {
        this.openPanel();
      }
    });

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closePanel();
      });
    }

    // If panel starts open in the rendered HTML, activate trap immediately
    if (this.panel.classList.contains('is-open')) {
      this.openPanel(true);
    }

    // ensure ARIA basics
    this.button.setAttribute('aria-controls', this.panel.id || '');
    this.button.setAttribute('aria-expanded', String(this.panel.classList.contains('is-open')));
    this.panel.setAttribute('aria-hidden', String(!this.panel.classList.contains('is-open')));
  }

  openPanel(skipClassAdd = false) {
    if (!skipClassAdd) this.panel.classList.add('is-open');

    document.body.classList.add('overlay_sizing', 'overflow-hidden');
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.setAttribute('aria-hidden', 'false');

    // Focus the close button (if present) or the first focusable element, otherwise the panel itself
    this.focusableElements = this.getFocusableElements();
    const elToFocus = this.closeButton || this.focusableElements[0] || this.panel;
    elToFocus.focus();

    // Add listeners
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('focusin', this.handleFocusIn);
  }

  closePanel() {
    this.panel.classList.remove('is-open');
    document.body.classList.remove('overlay_sizing', 'overflow-hidden');
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('aria-hidden', 'true');

    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('focusin', this.handleFocusIn);

    // Return focus to the button that opened the panel
    this.button.focus();
  }

  getFocusableElements() {
    const selector = [
      'a[href]',
      'area[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    // Only visible focusable elements (avoid display:none)
    return Array.from(this.panel.querySelectorAll(selector)).filter((el) => {
      // offsetParent is null for display:none; fall back to checking computed style
      return el.offsetParent !== null || window.getComputedStyle(el).visibility !== 'hidden';
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePanel();
      return;
    }

    if (event.key !== 'Tab') return;

    // refresh list each Tab (in case DOM changed)
    this.focusableElements = this.getFocusableElements();

    // nothing focusable inside -> keep focus on panel or closeButton
    if (this.focusableElements.length === 0) {
      event.preventDefault();
      (this.closeButton || this.panel).focus();
      return;
    }

    const firstEl = this.focusableElements[0];
    const lastEl = this.focusableElements[this.focusableElements.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      // Shift + Tab
      if (!this.panel.contains(active) || active === firstEl) {
        event.preventDefault();
        lastEl.focus();
      }
    } else {
      // Tab
      if (!this.panel.contains(active) || active === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }
  }

  handleFocusIn(event) {
    // If panel is open and focus moved outside it, bring it back
    if (!this.panel.classList.contains('is-open')) return;
    if (!this.panel.contains(event.target)) {
      const focusable = this.getFocusableElements();
      (focusable.length ? focusable[0] : this.closeButton || this.panel).focus();
    }
  }

  handleDocumentClick(event) {
    // click outside the custom element closes the panel
    if (!this.contains(event.target)) {
      this.closePanel();
    }
  }
}

customElements.define('pdp-sizing-toggle', PdpSizingToggle);




function addToFittingRoom(e, fallback) {
  const form = document.querySelector('form[action="/cart/add"]');
  const variantInput = form.querySelector('input[name="id"]');
  let selectedVariant;

  if (!variantInput) {
    console.warn('Variant input not found in form.');
    return;
  }

  const selectedVariantId = variantInput.value;
  console.log('Variant ID:', selectedVariantId);

  try {
    selectedVariant = ShopifyAnalytics.meta.product.variants.find(
      v => parseInt(v.id) === parseInt(selectedVariantId)
    );
    console.log('Selected Variant:', selectedVariant);

    _tote("event", "addToCart", {
      name: "addToCart",
      productId: e.target.dataset.productId,
      productTitle: e.target.dataset.productTitle,
      productVariant: selectedVariant,
      productImage: e.target.dataset.productImage
    });
  } catch (err) {
    console.error('Error in addToFittingRoom:', err);
  }
}





